import type { Instant, StudyStoreMap, StudyStoreName, UUID } from '@hoedokloop/domain';

import type {
  CapabilityResult,
  DeviceCredential,
  MediaRoomConnection,
  NotificationPermission,
  NotificationSubscription,
  PlatformAdapters,
  Purchase,
  RepositoryMigration,
  TrashPurgeResult,
} from './ports';

export function unsupportedResult<Value>(observedAt: Instant): CapabilityResult<Value> {
  return { ok: false, reason: 'unsupported', retryable: false, observedAt };
}

export function createUnsupportedPlatformAdapters(now: () => Instant): PlatformAdapters {
  const unsupportedAsync = async <Value>(): Promise<CapabilityResult<Value>> =>
    unsupportedResult(now());
  const unsupportedSync = <Value>(): CapabilityResult<Value> => unsupportedResult(now());

  return {
    api: {
      request: async () => {
        throw new Error('API transport is unavailable');
      },
    },
    studyRepository: {
      open: () => unsupportedAsync<{ schemaVersion: number }>(),
      close: () => unsupportedAsync<void>(),
      get: <Store extends StudyStoreName>(): Promise<
        CapabilityResult<StudyStoreMap[Store] | undefined>
      > => unsupportedAsync(),
      list: <Store extends StudyStoreName>(): Promise<CapabilityResult<StudyStoreMap[Store][]>> =>
        unsupportedAsync(),
      put: <Store extends StudyStoreName>(): Promise<CapabilityResult<StudyStoreMap[Store]>> =>
        unsupportedAsync(),
      putMany: <Store extends StudyStoreName>(): Promise<
        CapabilityResult<StudyStoreMap[Store][]>
      > => unsupportedAsync(),
      putBatch: () => unsupportedAsync<void>(),
      remove: () => unsupportedAsync<void>(),
      deletePermanent: () => unsupportedAsync<void>(),
      purgeTrash: () => unsupportedAsync<TrashPurgeResult>(),
      transaction: <Value>(): Promise<CapabilityResult<Value>> => unsupportedAsync(),
      migrations: (): Promise<CapabilityResult<RepositoryMigration[]>> => unsupportedAsync(),
    },
    identity: {
      getInstallationId: () => unsupportedAsync<UUID>(),
      getPlatformSubject: () => unsupportedAsync<string>(),
      getDeviceCredential: () => unsupportedAsync<DeviceCredential | undefined>(),
      issueDeviceCredential: () => unsupportedAsync<DeviceCredential>(),
      rotateDeviceCredential: () => unsupportedAsync<DeviceCredential>(),
      revokeDeviceCredential: () => unsupportedAsync<void>(),
    },
    notifications: {
      permission: () => unsupportedAsync<NotificationPermission>(),
      requestPermission: () => unsupportedAsync<NotificationPermission>(),
      subscribe: () => unsupportedAsync<NotificationSubscription>(),
      unsubscribe: () => unsupportedAsync<void>(),
    },
    camera: {
      permission: () => unsupportedAsync<NotificationPermission>(),
      capture: () => unsupportedAsync(),
      selectFromAlbum: () => unsupportedAsync(),
    },
    mediaRoom: {
      join: () => unsupportedAsync<MediaRoomConnection>(),
      leave: () => unsupportedAsync<void>(),
      setCameraEnabled: () => unsupportedAsync<void>(),
      setMicrophoneEnabled: () => unsupportedAsync<void>(),
      subscribeState: () => unsupportedSync(),
    },
    navigation: {
      back: () => unsupportedSync<void>(),
      close: () => unsupportedSync<void>(),
      open: () => unsupportedSync<void>(),
      openExternal: () => unsupportedSync<void>(),
    },
    share: {
      share: () => unsupportedAsync<void>(),
      save: () => unsupportedAsync<void>(),
    },
    lifecycle: {
      current: () => unsupportedSync(),
      isOnline: () => unsupportedSync(),
      subscribeState: () => unsupportedSync(),
      subscribeOnline: () => unsupportedSync(),
    },
    secureStorage: {
      get: () => unsupportedAsync(),
      set: () => unsupportedAsync<void>(),
      remove: () => unsupportedAsync<void>(),
    },
    payments: {
      purchase: () => unsupportedAsync<Purchase>(),
      restore: () => unsupportedAsync<Purchase[]>(),
    },
    analytics: {
      send: () => unsupportedAsync<{ accepted: number }>(),
      consent: () => 'undecided',
      setConsent: () => unsupportedSync<void>(),
      subscribeConsent: () => unsupportedSync(),
    },
    errors: {
      capture: () => unsupportedSync(),
    },
    haptics: {
      trigger: () => unsupportedSync(),
    },
  };
}
